import { CloudUpload, Delete as DeleteIcon, Image } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import api from 'services/api';

interface SaveDateFotoUploadProps {
  listaId: string;
  isOpen?: boolean;
  onSuccess?: (photoUrl: string | null) => void;
  onError?: (error: string) => void;
}

const SaveDateFotoUpload: React.FC<SaveDateFotoUploadProps> = ({
  listaId,
  isOpen = true,
  onSuccess,
  onError,
}) => {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBlobUrl = (value?: string | null): value is string =>
    typeof value === 'string' && value.startsWith('blob:');

  const responseHasImageBlob = (status: number, data: unknown): data is Blob => {
    return status !== 204 && data instanceof Blob && data.size > 0;
  };

  const compressImage = (file: File, maxWidth = 1600, quality = 0.82): Promise<File> =>
    new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
      img.src = objectUrl;
    });

  // Load existing photo on mount
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadPhoto = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/listas/${listaId}/save-date-foto/view?ts=${Date.now()}`, {
          responseType: 'blob',
        });
        if (!responseHasImageBlob(response.status, response.data)) {
          setPreview((current) => {
            if (isBlobUrl(current)) URL.revokeObjectURL(current);
            return null;
          });
          return;
        }

        const url = URL.createObjectURL(response.data);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setPreview((current) => {
          if (isBlobUrl(current)) URL.revokeObjectURL(current);
          return url;
        });
      } catch (err) {
        // Photo doesn't exist yet, which is fine
        setPreview((current) => {
          if (isBlobUrl(current)) URL.revokeObjectURL(current);
          return null;
        });
      } finally {
        setLoading(false);
      }
    };

    loadPhoto();

    return () => {
      cancelled = true;
    };
  }, [listaId, isOpen]);

  useEffect(() => {
    return () => {
      if (isBlobUrl(preview)) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      const msg = 'Apenas JPEG, PNG e WebP são permitidos';
      setError(msg);
      onError?.(msg);
      return;
    }

    // Validate raw size before compression (hard cap at 20MB to avoid hanging the browser)
    if (file.size > 20 * 1024 * 1024) {
      const msg = 'Arquivo muito grande (máximo 20MB antes da compressão)';
      setError(msg);
      onError?.(msg);
      return;
    }

    setUploading(true);
    setError(null);

    // Compress image client-side before upload so it always fits within proxy limits
    let uploadFile = file;
    try {
      uploadFile = await compressImage(file);
    } catch {
      // compression failed – try with original
    }

    // Show compressed preview immediately
    const previewReader = new FileReader();
    previewReader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    previewReader.readAsDataURL(uploadFile);

    try {
      const formData = new FormData();
      formData.append('foto', uploadFile);

      const response = await api.post(`/listas/${listaId}/save-date-foto`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.sucesso) {
        const photoResponse = await api.get(
          `/listas/${listaId}/save-date-foto/view?ts=${Date.now()}`,
          {
            responseType: 'blob',
          }
        );
        if (!responseHasImageBlob(photoResponse.status, photoResponse.data)) {
          setPreview((current) => {
            if (isBlobUrl(current)) URL.revokeObjectURL(current);
            return null;
          });
          return;
        }

        const blobUrl = URL.createObjectURL(photoResponse.data);
        setPreview((current) => {
          if (isBlobUrl(current)) URL.revokeObjectURL(current);
          return blobUrl;
        });
        onSuccess?.(blobUrl);
      }
    } catch (err: any) {
      // When nginx/proxy rejects with 413 before Express, the browser sees a CORS/network
      // failure. Detect this case and show a meaningful message.
      const httpStatus: number = err?.response?.status ?? err?.request?.status ?? 0;
      let msg: string;
      if (httpStatus === 413 || (!err.response && err.request)) {
        msg = 'Arquivo muito grande para envio. Tente uma imagem menor.';
      } else {
        msg = err?.response?.data?.erro || 'Erro ao fazer upload da foto';
      }
      setError(msg);
      onError?.(msg);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    setError(null);

    try {
      await api.delete(`/listas/${listaId}/save-date-foto`);
      setPreview((current) => {
        if (isBlobUrl(current)) URL.revokeObjectURL(current);
        return null;
      });
      onSuccess?.(null);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      const msg = err.response?.data?.erro || 'Erro ao deletar foto';
      setError(msg);
      onError?.(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        <Typography variant="h6">Foto do Save the Date</Typography>

        {error && <Alert severity="error">{error}</Alert>}

        {preview ? (
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: 1,
                mb: 2,
              }}
            />
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={uploading}
                onClick={handleBrowseClick}
              >
                {uploading ? 'Enviando...' : 'Alterar foto'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteConfirm(true)}
                disabled={uploading}
              >
                Deletar
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '2px dashed #ccc',
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'all 0.3s',
              backgroundColor: '#f9f9f9',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f0f0f0',
              },
            }}
            onClick={handleBrowseClick}
          >
            <Image
              sx={{
                fontSize: 48,
                color: '#999',
                mb: 1,
              }}
            />
            <Typography variant="body1" color="textSecondary" mb={1}>
              Arraste a imagem aqui ou clique para selecionar
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Formatos aceitos: JPEG, PNG, WebP — imagem comprimida automaticamente
            </Typography>
            {uploading && (
              <Box mt={2}>
                <CircularProgress size={24} />
                <Typography variant="caption" display="block" mt={1}>
                  Processando imagem...
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInputChange}
        />
      </Stack>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja deletar a foto do save the date?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={uploading}>
            {uploading ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SaveDateFotoUpload;
